import {Button} from '@dropbox/dig-components/buttons';
import {Keycap} from '@dropbox/dig-components/keycap';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {LightbulbLine, PersonLine, TeamLine} from '@dropbox/dig-icons/assets';
import {IS_APPLE} from '@lexical/utils';
import {
  logInstantAnswerAskClicked,
  logInstantAnswerClicked,
  logInstantAnswerShown,
  logInstantAnswerSummaryShown,
} from 'analytics/instantAnswerAnalytics';
import {knowledgeLinkState} from 'analytics/knowledgeAnalytics';
import {openAiSearchAndAskAtom} from 'atoms/aiSearch';
import {showSearchAtom} from 'atoms/search';
import {InstantAnswer, InstantAnswerPerson} from 'client';
import {handOffSpeculativeRun} from 'components/ai/speculativeAsk';
import {Avatar} from 'components/DSYS/Avatar';
import {Link} from 'components/DSYS/Link';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {useEffect, useRef} from 'react';

import {InstantAnswerSummaryLine} from './InstantAnswerSummaryLine';
import {useInstantAnswer} from './useInstantAnswer';
import {useInstantAnswerSummary} from './useInstantAnswerSummary';

const KIND_ICON = {
  [InstantAnswer.kind.KNOWLEDGE]: LightbulbLine,
  [InstantAnswer.kind.MANAGER]: PersonLine,
  [InstantAnswer.kind.OWNER]: TeamLine,
};

const MAX_PEOPLE = 3;

const isExternalUrl = (url: string) => /^https?:\/\//.test(url);

/** ServiceNow sends "YYYY-MM-DD hh:mm:ss"; the card shows the date only. */
const formatUpdatedOn = (value?: string | null) => {
  const date = value ? new Date(`${value.slice(0, 10)}T00:00:00`) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'})
    : null;
};

const People = ({people, onOpen}: {people: InstantAnswerPerson[]; onOpen: () => void}) => (
  <Split gap="16" wrap="wrap" alignY="center">
    {people.slice(0, MAX_PEOPLE).map((person) => (
      <Split.Item key={person.user_id}>
        <Link to={`/people/${person.ldap}`} onClick={onOpen} hasNoUnderline monochromatic>
          <Split gap="8" alignY="center">
            <Split.Item>
              <Avatar user={person} size="small" />
            </Split.Item>
            <Split.Item>
              <Text isBold size="small">
                {person.name}
              </Text>
              {person.role && (
                <Text size="small" color="faint">
                  {' '}
                  · {person.role}
                </Text>
              )}
            </Split.Item>
          </Split>
        </Link>
      </Split.Item>
    ))}
    {people.length > MAX_PEOPLE && (
      <Split.Item>
        <Text size="small" color="faint">
          {t('search_instant_answer_more_people', {count: people.length - MAX_PEOPLE})}
        </Text>
      </Split.Item>
    )}
  </Split>
);

/**
 * A sourced answer above the results for a question-shaped query, built
 * without a model (`GET /api/v1/search/instant_answer`). Renders nothing until
 * the server has a confident answer. A knowledge answer then gets a
 * one-sentence summary from its article, a second or so later.
 */
export const InstantAnswerCard = ({
  query,
  isOnlyOption,
}: {
  query: string;
  isOnlyOption: boolean;
}) => {
  const {data} = useInstantAnswer(query);
  const openAiSearchAndAsk = useSetAtom(openAiSearchAndAskAtom);
  const setShowSearch = useSetAtom(showSearchAtom);
  const loggedRef = useRef<string | null>(null);
  const loggedSummaryRef = useRef<string | null>(null);

  const answer = data?.answer ?? null;
  const answeredQuery = data?.query ?? '';
  const summaryQuery = useInstantAnswerSummary({answer, answeredQuery, liveQuery: query});
  const summary = summaryQuery.data?.summary ?? null;
  const summaryState = {
    shown: summary !== null,
    latencyMs: summary !== null ? (summaryQuery.data?.latencyMs ?? null) : null,
  };

  useEffect(() => {
    if (!data?.answer) {
      return;
    }
    const key = `${data.query}\n${data.answer.kind}\n${data.answer.url ?? data.answer.title}`;
    if (loggedRef.current === key) {
      return;
    }
    loggedRef.current = key;
    logInstantAnswerShown({answer: data.answer, query: data.query, latencyMs: data.latencyMs});
  }, [data]);

  const summaryData = summaryQuery.data;
  useEffect(() => {
    if (!answer || !summaryData?.summary) {
      return;
    }
    const key = `${answeredQuery}\n${answer.article_id}`;
    if (loggedSummaryRef.current === key) {
      return;
    }
    loggedSummaryRef.current = key;
    logInstantAnswerSummaryShown({answer, query: answeredQuery, latencyMs: summaryData.latencyMs});
  }, [answer, answeredQuery, summaryData]);

  if (!answer) {
    return null;
  }

  const handleOpen = (url: string) => () => {
    logInstantAnswerClicked({answer, query: answeredQuery, summary: summaryState});
    if (!isExternalUrl(url)) {
      setShowSearch(false);
    }
  };

  const handleAsk = () => {
    logInstantAnswerAskClicked({answer, query: answeredQuery, summary: summaryState});
    // The drawer takes over the turn search may have started for this query.
    handOffSpeculativeRun(query);
    openAiSearchAndAsk(query, 'search_instant_answer');
  };

  const updatedOn = formatUpdatedOn(answer.updated_on);
  const title = (
    <Text isBold size="large">
      <Truncate lines={1} maxWidth={640}>
        {answer.title}
      </Truncate>
    </Text>
  );

  return (
    <Box
      role="region"
      aria-label={t('search_instant_answer_label')}
      marginTop="12"
      padding="16"
      borderRadius="Medium"
      borderStyle="Solid"
      borderWidth="1"
      borderColor="Border Subtle"
      backgroundColor="Background Raised"
    >
      <Stack gap="8">
        <Split gap="4" alignY="center">
          <Split.Item>
            <UIIcon
              src={KIND_ICON[answer.kind]}
              size="small"
              className={atoms({color: 'Text Subtle'})}
            />
          </Split.Item>
          <Split.Item>
            <Text size="small" color="faint">
              {updatedOn
                ? t('search_instant_answer_source_updated', {
                    source: answer.source,
                    date: updatedOn,
                  })
                : answer.source}
            </Text>
          </Split.Item>
        </Split>
        {answer.url ? (
          <Link
            to={answer.url}
            openInNewTab={isExternalUrl(answer.url)}
            state={
              answer.kind === InstantAnswer.kind.KNOWLEDGE
                ? knowledgeLinkState('search')
                : undefined
            }
            onClick={handleOpen(answer.url)}
            hasNoUnderline
            showUnderlineOnHover
            monochromatic
          >
            {title}
          </Link>
        ) : (
          title
        )}
        <InstantAnswerSummaryLine
          summary={summary}
          isLoading={summaryQuery.fetchStatus === 'fetching'}
        />
        {answer.excerpt && (
          <Text color="subtle">
            <Truncate lines={3}>{answer.excerpt}</Truncate>
          </Text>
        )}
        {answer.people && answer.people.length > 0 && (
          <People people={answer.people} onOpen={handleOpen('/people')} />
        )}
        <Split gap="8" alignY="center">
          <Split.Item width="fill" />
          <Split.Item width="hug">
            <Button variant="transparent" size="small" onClick={handleAsk}>
              <Split gap="8" alignY="center">
                <Split.Item>{t('search_instant_answer_ask_for_more')}</Split.Item>
                <Split.Item>
                  <Keycap.Group
                    display="inline"
                    variant="outline"
                    size="small"
                    withAccessibleAdjacentLabel="and"
                    keycaps={
                      isOnlyOption
                        ? [{symbol: 'return', withAccessibleLabel: 'Enter'}]
                        : [
                            {
                              symbol: IS_APPLE ? 'command' : 'control',
                              withAccessibleLabel: IS_APPLE ? 'Command' : 'Control',
                            },
                            {symbol: 'return', withAccessibleLabel: 'Enter'},
                          ]
                    }
                  />
                </Split.Item>
              </Split>
            </Button>
          </Split.Item>
        </Split>
      </Stack>
    </Box>
  );
};
